import { Controller } from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';

/**
 * Remet un turbo-frame `data-turbo-action="advance"` d'accord avec l'URL après
 * un retour ou une avance dans l'historique.
 *
 * Deux défauts de Turbo 8 (vérifiés sur 8.0.18), qui laissent l'URL dire
 * `?page=2` pendant que le frame montre encore l'entreprise filtrée :
 *
 * 1. Une navigation de frame en `advance` pousse l'URL sans mettre à jour
 *    `view.lastRenderedLocation`. Au retour vers l'URL où la page a été rendue,
 *    `Navigator#locationWithActionIsSamePage` conclut « même page » : Turbo ne
 *    recharge rien. On le traite au `popstate`.
 * 2. Ailleurs dans l'historique, Turbo restaure un instantané de page dont le
 *    frame peut porter un contenu (et un `src`) d'une autre URL. Le frame
 *    restauré se reconnecte : on le traite au `connect`.
 *
 * Dans les deux cas, si le `src` du frame (la dernière URL qu'il a chargée)
 * diffère de l'URL affichée, on le recharge sur cette URL en `replace`, sans
 * nouvelle entrée d'historique. Un frame rendu par le serveur n'a pas de `src` :
 * rien ne se passe au chargement normal d'une page.
 *
 * @example
 *   <turbo-frame id="prorata_contributions_table" data-turbo-action="advance"
 *                data-controller="frame-history-sync">…</turbo-frame>
 */
export default class extends Controller {
  connect() {
    // L'URL où Turbo a rendu la page, donc celle qu'il jugera « même page ».
    this.renderedAt = window.location.href;
    this.popHandler = () => {
      if (window.location.href === this.renderedAt) setTimeout(() => this.resync());
    };
    window.addEventListener('popstate', this.popHandler);
    this.resync();
  }

  disconnect() {
    window.removeEventListener('popstate', this.popHandler);
  }

  resync() {
    const frame = this.element;
    if (!frame.isConnected || !frame.src) return;
    if (new URL(frame.src, window.location.href).href === window.location.href) return;

    Turbo.visit(window.location.href, { frame: frame.id, action: 'replace' });
  }
}
